/**
 * Post-authentication destination helpers.
 *
 * Models Reddit's `?dest=` pattern so a signed-out viewer who tried to
 * reach `/chat` (directly, or via a shared `/signin?dest=/chat` link)
 * lands back on `/chat` after successful authentication — including
 * after an OAuth popup / redirect round-trip.
 *
 * Security contract:
 * - Only same-origin, path-prefixed destinations are honored
 *   (`/chat`, `/settings/privacy?foo=1`).
 * - Absolute URLs, protocol-relative URLs (`//evil.com`), any string
 *   containing a scheme (`:`) in the path portion, Windows back-slash
 *   paths, and strings with control characters are rejected outright.
 *   This closes the classic "open redirect" attack where a hostile
 *   referer controls `?dest=` and sends the viewer to a phishing
 *   page after sign-in.
 * - Known auth routes (`/signin`, `/signup`, `/passwordrecovery`,
 *   `/password`, `/welcome`) are rejected as destinations to prevent
 *   auth loops and to avoid sending viewers back into the sign-in
 *   flow they just completed.
 * - Destination length is capped at 2 KiB to bound URL/storage size.
 *
 * Survival contract:
 * - Pending destinations are stored in `sessionStorage` (not
 *   `localStorage`) so they do not leak across unrelated browser
 *   sessions. Session storage survives OAuth popup/redirect
 *   round-trips because the origin is preserved.
 * - The module is written to be safe to import server-side; all
 *   `window` / `sessionStorage` access is guarded.
 */

const PENDING_DEST_STORAGE_KEY = "societea:auth:pending-dest"
const DEST_MAX_LENGTH = 2048
const DEST_QUERY_PARAM = "dest"

/**
 * Paths that must never be used as post-auth destinations. Either
 * because they are part of the auth flow itself (would cause a loop)
 * or because they are one-shot product chrome that should not be
 * re-opened after a refresh.
 */
const DISALLOWED_DEST_PREFIXES: ReadonlyArray<string> = [
    "/signin",
    "/signup",
    "/passwordrecovery",
    "/password",
    "/welcome",
]

/**
 * Safe-guarded accessor for `window.sessionStorage`. Returns `null`
 * in non-browser environments (SSR) or when storage is disabled
 * (Safari private mode, storage-partitioned iframes).
 */
function getSessionStorageSafe(): Storage | null {
    if (typeof window === "undefined") {
        return null
    }
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

/**
 * Returns the sanitized destination if the input is a safe
 * same-origin relative path, otherwise `null`.
 *
 * The sanitizer is deliberately strict: any uncertainty returns
 * `null`. Callers should treat a `null` return as "fall back to the
 * default landing page" (`/`).
 */
export function sanitizeDestination(
    rawDest: string | null | undefined
): string | null {
    if (typeof rawDest !== "string") {
        return null
    }
    if (rawDest.length === 0 || rawDest.length > DEST_MAX_LENGTH) {
        return null
    }
    // Reject control chars, NUL, DEL, and back-slash (Windows paths
    // are treated as unsafe even though they rarely appear; rejecting
    // keeps the parser small).
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f\\]/.test(rawDest)) {
        return null
    }
    // Must start with a single leading slash so the destination is
    // interpreted relative to the current origin.
    if (!rawDest.startsWith("/")) {
        return null
    }
    // `//anything` is protocol-relative and resolves to another
    // origin. Always reject.
    if (rawDest.startsWith("//")) {
        return null
    }
    const [pathOnly] = rawDest.split(/[?#]/, 1)
    // Defense in depth: any colon in the path portion implies a
    // scheme (e.g. `javascript:`) was smuggled in via unicode or
    // mixed-case. Reject.
    if (pathOnly.includes(":")) {
        return null
    }
    // Reject direct navigation to auth / onboarding routes.
    const normalizedPath = pathOnly.toLowerCase()
    for (const disallowed of DISALLOWED_DEST_PREFIXES) {
        if (
            normalizedPath === disallowed ||
            normalizedPath.startsWith(`${disallowed}/`)
        ) {
            return null
        }
    }
    return rawDest
}

/**
 * Parses a `dest` query-string parameter out of a `location.search`
 * string and returns the sanitized destination, or `null` when the
 * parameter is missing or the value fails validation.
 */
export function parseDestinationFromSearch(
    search: string | null | undefined
): string | null {
    if (typeof search !== "string" || search.length === 0) {
        return null
    }
    try {
        const params = new URLSearchParams(
            search.startsWith("?") ? search.slice(1) : search
        )
        const raw = params.get(DEST_QUERY_PARAM)
        return sanitizeDestination(raw)
    } catch {
        return null
    }
}

/**
 * Builds a locale-agnostic signin URL with a `dest` query param.
 * Returns `/signin` (or `/signup`) when no destination is available.
 *
 * Note: the CRA router does not use a locale prefix for `/signin`;
 * the Next port will layer locale prefixes on top of this helper
 * when the auth surfaces migrate.
 */
export function buildSigninUrl(
    dest: string | null | undefined = null,
    mode: "signin" | "signup" = "signin"
): string {
    const basePath = mode === "signup" ? "/signup" : "/signin"
    const sanitized = sanitizeDestination(dest)
    if (!sanitized) {
        return basePath
    }
    const params = new URLSearchParams()
    params.set(DEST_QUERY_PARAM, sanitized)
    return `${basePath}?${params.toString()}`
}

/**
 * Serializes the current browser location (pathname + search + hash)
 * into a sanitized destination string, or `null` if the current
 * location is not a safe destination (e.g. already on `/signin`,
 * running under SSR).
 */
export function getCurrentLocationAsDestination(): string | null {
    if (typeof window === "undefined") {
        return null
    }
    const { pathname, search, hash } = window.location
    const composed = `${pathname}${search}${hash}`
    return sanitizeDestination(composed)
}

/**
 * Stores a pending post-auth destination in session storage.
 * Silently no-ops when the destination fails validation or storage
 * is unavailable.
 */
export function storePendingDestination(dest: string | null | undefined): void {
    const sanitized = sanitizeDestination(dest)
    if (!sanitized) {
        return
    }
    const session = getSessionStorageSafe()
    if (!session) {
        return
    }
    try {
        session.setItem(PENDING_DEST_STORAGE_KEY, sanitized)
    } catch {
        // sessionStorage may be full/disabled; pending-dest is a
        // best-effort convenience, not a correctness invariant.
    }
}

/**
 * Reads the pending destination without clearing it. Used when we
 * want to check whether a redirect is pending (e.g. to decide
 * between "default landing" and "targeted landing") before actually
 * consuming it.
 */
export function peekPendingDestination(): string | null {
    const session = getSessionStorageSafe()
    if (!session) {
        return null
    }
    try {
        return sanitizeDestination(session.getItem(PENDING_DEST_STORAGE_KEY))
    } catch {
        return null
    }
}

/**
 * Reads and clears the pending destination atomically. Returns the
 * sanitized destination string, or `null` when no valid destination
 * is pending.
 */
export function consumePendingDestination(): string | null {
    const session = getSessionStorageSafe()
    if (!session) {
        return null
    }
    try {
        const raw = session.getItem(PENDING_DEST_STORAGE_KEY)
        session.removeItem(PENDING_DEST_STORAGE_KEY)
        return sanitizeDestination(raw)
    } catch {
        return null
    }
}

/**
 * Clears any pending destination. Useful for tests and for flows
 * that intentionally want to cancel a previously captured intent
 * (e.g. the viewer explicitly navigated away from the guarded
 * surface without authenticating).
 */
export function clearPendingDestination(): void {
    const session = getSessionStorageSafe()
    if (!session) {
        return
    }
    try {
        session.removeItem(PENDING_DEST_STORAGE_KEY)
    } catch {
        // ignore
    }
}
